'use client';

import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/nextjs';
import type { SentryIdentity } from '@/lib/telemetry/sentry-identity';
import { apiFetch } from '@/lib/utils/api-fetch';

const IDENTITY_URL = '/api/v1/sentry-identity';

// Matches the gate in `instrumentation-client.ts`. Without it this fires a
// request on every layout mount in an environment that has no Sentry to send
// the answer to.
const SENTRY_ENABLED = Boolean(process.env.NEXT_PUBLIC_SENTRY_DSN);

/**
 * Attaches the signed-in identity and the org's flag state to every subsequent
 * browser event. Renders nothing.
 *
 * Mounted in the root layout, which /sign-in and /welcome render too, so an
 * unauthenticated visit resolves to `null` and clears whatever the last session
 * left behind rather than inheriting it. That matters on a shared machine and
 * on an account switch, neither of which reloads the tab.
 */
export default function SentryContext() {
  const appliedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!SENTRY_ENABLED) {
      return;
    }
    const controller = new AbortController();
    const pending = apiFetch(IDENTITY_URL, {
      signal: controller.signal,
    }) as Promise<SentryIdentity | null>;

    void pending
      .then((identity: SentryIdentity | null) => {
        if (!identity) {
          clearIdentity();
          appliedRef.current = null;
          return;
        }

        // An internal user switching accounts keeps the same email, so the
        // org has to be part of the key or the second account never gets
        // applied.
        const key = `${identity.email}:${identity.accountId}`;
        if (appliedRef.current === key) {
          return;
        }

        applyIdentity(identity);
        appliedRef.current = key;
      })
      // An abort or a dropped connection rejects, and an unhandled rejection
      // is a console error on every unmount. `apiFetch` has already logged a
      // real failure, and identity is context, never a reason to fail a page.
      .catch(() => {});

    return () => {
      controller.abort();
    };
  }, []);

  return null;
}

function applyIdentity(identity: SentryIdentity): void {
  Sentry.setUser({ id: identity.email });
  Sentry.setTag('org', identity.orgSlug);
  Sentry.setTag('account', identity.accountId);
  Sentry.setTag('impersonated', identity.impersonated);

  const flags =
    Sentry.getClient()?.getIntegrationByName<Sentry.FeatureFlagsIntegration>(
      'FeatureFlags'
    );
  if (!flags) {
    return;
  }
  // Flag key and boolean result only. The LaunchDarkly evaluation context is
  // an org, and its slug is already a tag, so nothing extra reaches Sentry
  // through this path.
  for (const [key, value] of Object.entries(identity.flags)) {
    flags.addFeatureFlag(key, value);
  }
}

function clearIdentity(): void {
  Sentry.setUser(null);
  Sentry.setTag('org', undefined);
  Sentry.setTag('account', undefined);
  Sentry.setTag('impersonated', undefined);
}
